import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, functions } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Row, Col, Button, Card, Alert, Container } from 'react-bootstrap';
import NavBar from '../Components/NavBar.js';
import DeletePendingPlayerRequest from '../Modules/DeletePendingPlayerRequest.js';
import Globals from '../Globals.js';
const AppGlobals = Globals;

const ApprovePlayerRequests = () => {
  const { teamName } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionRequestId, setActionRequestId] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [currentTask, setCurrentTask] = useState('');
  const [selectedPlayerName, setSelectedPlayerName] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        loadRequests();
      } else {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [teamName]);

  const loadRequests = async () => {
    setError('');
    setLoading(true);
    try {
      const listPending = httpsCallable(functions, 'listPendingPlayerRequests');
      const result = await listPending({ teamName });
      setRequests(result.data.requests || []);
      setIsAuthorized(true);
    } catch (err) {
      if (err.code === 'permission-denied') {
        setIsAuthorized(false);
        setError('You are not authorized to review player requests for this team.');
      } else {
        setError(err.message || 'Unable to load player requests.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setError('');
    setActionRequestId(requestId);
    try {
      const approveRequest = httpsCallable(functions, 'approvePlayerRequest');
      await approveRequest({ requestId });
      await loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to approve player request.');
    } finally {
      setActionRequestId('');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    window.localStorage.removeItem('currentTeam');
    navigate('/');
  };

  const handleDeletePlayerRequest = (requestId, playerName) => {
    setError('');
    setCurrentTask(AppGlobals.taskNumbers.deletePlayerRequest);
    setActionRequestId(requestId);
    setSelectedPlayerName(playerName);
  }

  if (!user) {
    return (
      <>
        <NavBar pageName="Approve Player Requests" showMenu={false} />
        <Container className="my-4">
          <Row>
            <Col md={8} lg={6} className="mx-auto">
              <Card>
                <Card.Body>
                  <Card.Title>Team Admin Sign In</Card.Title>
                  <Card.Text>Please sign in with your team admin account to review pending player requests.</Card.Text>
                  <Button onClick={() => navigate(`/manage-team/${teamName}`)}>Sign In to Manage Team</Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  if (user && !isAuthorized) {
    return (
      <>
        <NavBar pageName="Approve Player Requests" showMenu={false} />
        <Container className="my-4">
          <Row>
            <Col md={8} lg={6} className="mx-auto">
              <Card>
                <Card.Body>
                  <Card.Title>Not Authorized</Card.Title>
                  <Card.Text>Your account is not authorized to approve player requests for {teamName}.</Card.Text>
                  {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                  <Button variant="outline-primary" className="me-2"  onClick={() => navigate(`/manage-team/${teamName}`)}>Back to Manage Team</Button>
                  <Button variant="danger" onClick={handleLogout}>Sign Out</Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }


  return (
    <>
      {currentTask === AppGlobals.taskNumbers.deletePlayerRequest && <DeletePendingPlayerRequest currentTask={currentTask} setCurrentTask={setCurrentTask} teamName={teamName} actionRequestId={actionRequestId} selectedPlayerName={selectedPlayerName} /> }
      <NavBar teamName={teamName} showMenu={true} pageName="Approve Player Requests" />
      <Container className="my-4">
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <h2>Pending Player Requests</h2>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">Loading player requests...</div>
        ) : requests.length === 0 ? (
          <Alert variant="info">No pending player requests for {teamName}.</Alert>
        ) : (
          requests.map((req) => (
            <Card className="mb-3" key={req.id}>
              <Card.Body>
                <Card.Title>{req.playerName}</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">{req.playerEmail} — {req.isSub ? 'Substitute' : 'Full-time'} Player</Card.Subtitle>
                <Card.Text>{req.note || 'No additional notes provided.'}</Card.Text>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    onClick={() => handleApprove(req.id)}
                    disabled={actionRequestId === req.id}
                  >
                    {actionRequestId === req.id && currentTask === AppGlobals.taskNumbers.approvePlayer ? 'Approving…' : 'Approve Player'}
                  </Button>
                  <Button variant="danger" onClick={() => handleDeletePlayerRequest(req.id, req.playerName)}>
                    {actionRequestId === req.id && currentTask === AppGlobals.taskNumbers.deletePlayerRequest ? 'Processing…' : 'Delete Request'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))
        )}
      </Container>
    </>
  );
};

export default ApprovePlayerRequests;
